import { observer } from 'mobx-react';
import * as React from 'react';
import { openOverlay, OverlayComponentProps } from '../overlay';
import { injectTSDI } from '../../core/tsdi';
import {
    ConnectApiRateBundleDto,
    ConnectApiRateBundleTermDto,
    PostActionPopupDto,
    PreSignContractConditionDto,
    StringWithLocaleDto
} from '../../core/api/dtos';
import { I18nStore } from '../../core/i18n-store';
import { StudioRequests } from '../../core/studio-store';
import { Button, ButtonClean } from '../button';
import { closeValidationPopup } from '../validation-popup';
import { facebookPixelTrack } from '../../core/analytics';
import { LoaderFullPage } from '../loader';
import { Theme } from '../../core/initialize-config-styles';
import { CreditCardQueryParams } from '../credit-card/interfaces';
import { FormattedTerm } from '../term/';
import { getStepComponent, getStepStore, RateWizardStep } from './steps';
import { RateBundlesWizardStore } from './store';

import './rate-wizard.scss';

export interface PostActionConfig {
    postAction?: 'SHOW_POPUP' | 'REDIRECT';
    postActionPopupDto?: PostActionPopupDto;
    postActionUrls?: StringWithLocaleDto[];

    onCloseAfterSuccess(): void;
}

export interface RateBundleWizardConfig {
    studioId: number | number[];
    studioRequests: StudioRequests;
    postActionConfig: PostActionConfig;
    landingPageConfigurationId?: number;
    contractSigningConditions?: PreSignContractConditionDto;
}

export const openBookRateBundleWizard = (
    rateBundle: ConnectApiRateBundleDto,
    term: ConnectApiRateBundleTermDto,
    config: RateBundleWizardConfig,
    theme: Theme,
    resumedCreditCardQuery?: CreditCardQueryParams
) => {
    const rateBundlesWizardStore = injectTSDI(RateBundlesWizardStore);
    rateBundlesWizardStore.init(rateBundle, term, config);

    if (resumedCreditCardQuery) {
        rateBundlesWizardStore.resumeOnCreditCard(resumedCreditCardQuery);
    }

    openOverlay({
        id: 'rate-wizard',
        component: (overlayProps) => (
            <RateBundleWizard theme={theme} {...overlayProps} />
        )
    });
};

interface RateBundleWizardProps {
    theme: Theme;
}

const RateBundleWizard = observer(
    (props: OverlayComponentProps & RateBundleWizardProps) => {
        const { currentStep, reset, bundle, term, studioRequest } = injectTSDI(
            RateBundlesWizardStore
        );

        React.useEffect(
            () => () => {
                reset();
                closeValidationPopup();
            },
            []
        );

        const facebookPixelData = {
            content_name: bundle?.name,
            value: term?.price,
            currency: 'EUR'
        };

        React.useEffect(() => {
            facebookPixelTrack('AddToCard', facebookPixelData);
        }, []);

        React.useEffect(() => {
            if (currentStep === 'ADDRESS') {
                facebookPixelTrack('InitiateCheckout', facebookPixelData);
            }
        }, [currentStep]);

        const Step = getStepComponent(currentStep);

        if (!studioRequest.fetched) {
            <LoaderFullPage />;
        }

        return (
            <div className="wizard" data-role="rate-bundles-wizard">
                <Nav />
                <Header />
                <button
                    className="overlay__close"
                    onClick={props.closeOverlay}
                />
                <div>
                    <div
                        className={classnames({
                            'wizard__slide-resumee': currentStep === 'SUMMARY'
                        })}
                    >
                        <Step theme={props.theme} />
                        <Buttons />
                    </div>
                </div>
            </div>
        );
    }
);

const Header = observer(() => {
    const { bundle, term } = injectTSDI(RateBundlesWizardStore);

    return (
        <div className="wizard__header">
            <h3>{bundle?.name}</h3>
            <div className="wizard__term-value">
                <p>
                    <FormattedTerm rateBundleTerm={term} />
                </p>
            </div>
        </div>
    );
});

const Nav = observer(() => {
    const { steps, currentStep, setCurrentStep } = injectTSDI(
        RateBundlesWizardStore
    );
    const { translate } = injectTSDI(I18nStore);

    const isStepDone = (step: RateWizardStep) =>
        steps.indexOf(currentStep) > steps.indexOf(step);

    const getStepClass = (step: RateWizardStep, ...otherClasses: string[]) =>
        classnames(
            'wizard-nav-item',
            {
                'wizard-nav-item--active': step === currentStep,
                'wizard-nav-item--done': isStepDone(step)
            },
            ...otherClasses
        );

    const onClickStep = (step: RateWizardStep) => {
        if (isStepDone(step)) {
            setCurrentStep(step);
        }
    };

    return (
        <div className="wizard__nav">
            <ul>
                {steps.map((step) => (
                    <li
                        key={step}
                        className={getStepClass(step)}
                        onClick={() => onClickStep(step)}
                    >
                        <span>{translate(getStepStore(step).labelKey)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
});

const Buttons = observer(() => {
    const {
        goNext,
        goPrev,
        submit,
        isFirstStep,
        isLastStep,
        validating,
        studioRequest
    } = injectTSDI(RateBundlesWizardStore);
    const { translate } = injectTSDI(I18nStore);

    return (
        <div className="wizard__button-wrapper">
            {!isFirstStep ? (
                <ButtonClean onClick={goPrev} data-role="back">
                    {translate('commons.backward')}
                </ButtonClean>
            ) : (
                <span />
            )}

            {isLastStep ? (
                <Button
                    color="primary"
                    onClick={submit}
                    data-role="sign-contract"
                    hover="scale"
                >
                    {translate('common.sign.contract')}
                </Button>
            ) : (
                <Button
                    color="primary"
                    onClick={goNext}
                    loading={validating || studioRequest.loading}
                    hover="scale"
                    data-role="next"
                >
                    {translate('commons.forward')}
                </Button>
            )}
        </div>
    );
});
